/**
 * Shared report value formatters — single source for React viewer,
 * DOCX export, and Score Provenance display.
 */
import type { ElectricityData, ConnectivityData } from '../types/siteRag';

/**
 * Canonical retail-rate normalizer → cents/kWh.
 *
 * The report's `retailRate` field is, by convention, in ¢/kWh (see
 * `ElectricityData` / the EIA provider, which returns the EIA v2
 * `retail-sales` `price` natively in cents). But document-extracted
 * values (`extractFromDocument.ts`) can arrive in $/kWh — a tariff PDF
 * reading "$0.08/kWh" lands as `retailRate = 0.08`, which then renders as
 * "0.08 ¢/kWh" (a 100× error) and propagates into every downstream binding.
 *
 * US industrial retail rates are ~4–15 ¢/kWh; a value below 1.0 is
 * physically implausible as cents but is exactly the $/kWh magnitude
 * (0.04–0.15 $/kWh), so we treat `0 < rate < 1` as dollars-per-kWh and
 * convert ×100. Values ≥ 1 are taken as already-canonical cents. This is
 * the SINGLE conversion point — every display path routes through it so
 * header and body can never diverge.
 *
 * Returns a number in ¢/kWh, or `null` when the input is not finite.
 */
export const USD_PER_KWH_TO_CENTS = 100; // 1 $/kWh = 100 ¢/kWh
/** Below this (in the raw field) a value is read as $/kWh, not ¢/kWh. */
export const RETAIL_RATE_DOLLARS_THRESHOLD_CENTS = 1.0;

export function normalizeRetailRateToCents(
  rate: number | null | undefined,
): number | null {
  if (rate == null || !Number.isFinite(rate)) return null;
  // Negative rates are non-physical — surface as null rather than guess.
  if (rate < 0) return null;
  // 0 < rate < 1 ⇒ value was $/kWh mislabeled as ¢/kWh ⇒ ×100.
  if (rate > 0 && rate < RETAIL_RATE_DOLLARS_THRESHOLD_CENTS) {
    return rate * USD_PER_KWH_TO_CENTS;
  }
  return rate;
}

/**
 * Format cents/kWh for display (EIA industrial retail rate).
 *
 * Routes through `normalizeRetailRateToCents` so a $/kWh input is corrected
 * to the canonical ¢/kWh value before rendering. `decimals` defaults to 2
 * (the shared default) so every caller renders the SAME value — header and
 * body cannot diverge via different rounding.
 */
export function formatElectricityRateCents(
  rate: number | null | undefined,
  decimals = 2,
): string | null {
  const cents = normalizeRetailRateToCents(rate);
  if (cents == null) return null;
  return `${cents.toFixed(decimals)} ¢/kWh`;
}

/** Explicit EIA context is already in cents; unqualified legacy values keep their dollar heuristic. */
export function normalizeElectricityPriceToCents(electricity: {
  retailRate?: number | null;
  basis?: 'state-industrial-average';
}): number | null {
  if (electricity.basis === 'state-industrial-average') {
    const rate = electricity.retailRate;
    return typeof rate === 'number' && Number.isFinite(rate) && rate >= 0 ? rate : null;
  }
  return normalizeRetailRateToCents(electricity.retailRate);
}

/** Scope-aware price text shared by narratives, report tables and comparisons. */
export function formatElectricityPriceReference(electricity: {
  retailRate?: number | null;
  state?: string;
  year?: number;
  basis?: 'state-industrial-average';
}): string {
  const label = electricity.basis === 'state-industrial-average'
    ? 'EIA state industrial average' : 'Electricity price reference';
  const cents = normalizeElectricityPriceToCents(electricity);
  const value = cents == null ? 'unavailable' : `${cents.toFixed(2)} ¢/kWh`;
  const location = typeof electricity.state === 'string' ? electricity.state.trim() : '';
  const year = typeof electricity.year === 'number' && Number.isInteger(electricity.year) && electricity.year > 0
    ? String(electricity.year) : '';
  const vintage = [location, year].filter(Boolean).join(', ');
  const scope = electricity.basis === 'state-industrial-average' ? ' — context only; not a site tariff' : '';
  return `${label}: ${value}${vintage ? ` (${vintage})` : ''}${scope}.`;
}

/** Compact accordion/export title; the body retains the full location, vintage and scope. */
export function formatElectricityPricingTitle(electricity: ElectricityData): string {
  const label = electricity.basis === 'state-industrial-average'
    ? 'EIA state average' : 'Electricity price reference';
  const cents = normalizeElectricityPriceToCents(electricity);
  return `${label}: ${cents == null ? 'unavailable' : `${cents.toFixed(2)} ¢/kWh`}`;
}

/** SED environmental slots use `wetlandAcres`; report datasets use `wetlandAcreage`. */
export function resolveWetlandAcreage(
  env: { wetlandAcreage?: number; wetlandAcres?: number; wetlandSourceAcreageConflicts?: unknown[]; wetlandAcreageIssues?: unknown[] } | null | undefined,
): number | undefined {
  if (!env || env.wetlandSourceAcreageConflicts?.length || env.wetlandAcreageIssues?.length) return undefined;
  const v = env.wetlandAcreage ?? env.wetlandAcres;
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/** Wetlands row for Extended Data / exports — avoids "Yes — — acres" when acreage lives on the SED field name. */
export function formatWetlandsDisplay(
  env: { hasWetlands?: boolean; wetlandAcreage?: number; wetlandAcres?: number; wetlandAcreageBasis?: 'source-feature-total'; wetlandSourceAcreageConflicts?: unknown[]; wetlandAcreageIssues?: unknown[] } | null | undefined,
): string {
  if (env?.hasWetlands === undefined) return 'Unknown';
  if (env.hasWetlands === false) return 'None';
  if (env.wetlandSourceAcreageConflicts?.length) return 'Yes (acreage unavailable: source records disagree)';
  const acres = resolveWetlandAcreage(env);
  if (acres == null) return 'Yes (acreage unavailable)';
  return `Yes – ${acres.toFixed(1)} acres${env.wetlandAcreageBasis ? ' (full source features; not parcel acreage)' : ''}`;
}

export function formatConnectivitySummary(data: {
  nearestIxName?: string;
  nearestIxDistKm?: number;
  nearestIx?: string;
  distanceKm?: number;
}): string | null {
  const name = data.nearestIxName ?? data.nearestIx;
  const km = data.nearestIxDistKm ?? data.distanceKm;
  if (!name && km == null) return null;
  if (name && km != null && Number.isFinite(km)) return `${name} (${km} km)`;
  if (name) return String(name);
  if (km != null && Number.isFinite(km)) return `${km} km`;
  return null;
}

/**
 * Format a provenance provider/doc value for human display.
 * Avoids raw JSON for structured connectivity / logistics objects.
 */
export function formatProvenanceValue(v: unknown): string {
  if (v === null || v === undefined) return '–';
  if (typeof v === 'number') return v.toString();
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string') return v.trim() || '–';
  if (typeof v === 'object' && !Array.isArray(v)) {
    const obj = v as Record<string, unknown>;
    const connectivity = formatConnectivitySummary({
      nearestIxName: typeof obj.nearestIxName === 'string' ? obj.nearestIxName : undefined,
      nearestIxDistKm: typeof obj.nearestIxDistKm === 'number' ? obj.nearestIxDistKm : undefined,
      nearestIx: typeof obj.nearestIx === 'string' ? obj.nearestIx : undefined,
      distanceKm: typeof obj.distanceKm === 'number' ? obj.distanceKm : undefined,
    });
    if (connectivity) return connectivity;
    if (typeof obj.capacity === 'number' || typeof obj.capacity === 'string') {
      return `${obj.capacity} MW`;
    }
    if (typeof obj.acres === 'number') return `${obj.acres} acres`;
    if (typeof obj.zoning === 'string') return obj.zoning;
    try {
      const s = JSON.stringify(v);
      return s.length > 60 ? `${s.slice(0, 57)}…` : s;
    } catch {
      return String(v);
    }
  }
  return String(v);
}

export interface ExtendedDataRow {
  label: string;
  value: string;
}

type DatasetShape = Partial<NonNullable<import('../types/siteRag').SiteRagReport['datasets']>>;

function formatDataValue(
  raw: string | number | null | undefined,
  unit?: string,
): string | null {
  if (raw == null) return null;
  if (typeof raw === 'number' && !Number.isFinite(raw)) return null;
  if (typeof raw === 'string' && (raw === 'NaN' || raw === 'null' || raw === 'undefined')) return null;
  const base = typeof raw === 'number' ? String(raw) : raw;
  return unit ? `${base} ${unit}` : base;
}

/** Build extended-data table rows matching ExtendedDataSection fields. */
export function buildExtendedDataRows(
  report: {
    address?: string;
    location?: { lat?: number; lng?: number };
    siteArea?: number;
    siteElevationRange?: { min: number; max: number; avg: number };
    datasets?: import('../types/siteRag').SiteRagReport['datasets'];
  },
): ExtendedDataRow[] {
  const ds: DatasetShape = report.datasets ?? {};
  const rows: ExtendedDataRow[] = [];

  if (report.address) rows.push({ label: 'Address', value: report.address });
  if (
    Number.isFinite(report.location?.lat)
    && Number.isFinite(report.location?.lng)
  ) {
    rows.push({
      label: 'Coordinates',
      value: `${report.location!.lat!.toFixed(4)}, ${report.location!.lng!.toFixed(4)}`,
    });
  }
  if (report.siteArea != null && Number.isFinite(report.siteArea)) {
    rows.push({ label: 'Site Area', value: `${report.siteArea.toFixed(1)} acres` });
  }
  if (report.siteElevationRange) {
    const r = report.siteElevationRange;
    const legacyZero = r.min === 0 && r.max === 0 && r.avg === 0;
    if (!legacyZero && Number.isFinite(r.min) && Number.isFinite(r.max)) {
      rows.push({
        label: 'Elevation',
        value: `${r.min}–${r.max}m (avg ${r.avg}m)`,
      });
    }
  }
  if (ds.climate) {
    const avgTemp = formatDataValue(ds.climate.avgTemp, '°C');
    if (avgTemp) rows.push({ label: 'Avg Temperature', value: avgTemp });
    const peakTemp = formatDataValue(ds.climate.peakTemp, '°C');
    if (peakTemp) rows.push({ label: 'Peak Temperature', value: peakTemp });
    const avgWb = formatDataValue(ds.climate.avgWetBulb, '°C');
    if (avgWb) rows.push({ label: 'Avg Wet Bulb', value: avgWb });
    if (ds.climate.waterStress != null) {
      rows.push({ label: 'Water Stress', value: String(ds.climate.waterStress) });
    }
    const humidity = formatDataValue(ds.climate.humidity, '%');
    if (humidity) rows.push({ label: 'Humidity', value: humidity });
  }
  if (ds.seismic) {
    if (ds.seismic.designCategory) {
      rows.push({ label: 'Seismic Design Category', value: `SDC ${ds.seismic.designCategory}` });
    }
    if (Number.isFinite(ds.seismic.pga)) rows.push({ label: 'PGA', value: `${ds.seismic.pga!.toFixed(3)}g` });
    if (Number.isFinite(ds.seismic.sds) && Number.isFinite(ds.seismic.sd1)) {
      rows.push({
        label: 'SDS / SD1',
        value: `${ds.seismic.sds!.toFixed(3)}g / ${ds.seismic.sd1!.toFixed(3)}g`,
      });
    }
  }
  if (ds.grid) {
    if (ds.grid.substationDist != null) rows.push({ label: 'Grid Distance', value: `${ds.grid.substationDist} km` });
    if (ds.grid.capacity != null) rows.push({ label: 'Grid Capacity', value: String(ds.grid.capacity) });
    if (ds.grid.voltage != null) rows.push({ label: 'Grid Voltage', value: String(ds.grid.voltage) });
  }
  if (ds.land?.floodRisk != null) rows.push({ label: 'Flood Risk', value: String(ds.land.floodRisk) });
  if (ds.solar?.ghi != null) rows.push({ label: 'Solar GHI', value: `${ds.solar.ghi} kWh/m²/day` });
  if (ds.wind && Number.isFinite(ds.wind.windSpeed100m) && Number.isFinite(ds.wind.capacityFactor)) {
    rows.push({
      label: 'Wind Speed (100m)',
      value: `${ds.wind.windSpeed100m} m/s (CF: ${(ds.wind.capacityFactor * 100).toFixed(0)}%)`,
    });
  }
  if (ds.electricity) {
    rows.push({ label: 'Electricity Price Reference', value: formatElectricityPriceReference(ds.electricity) });
  }
  if (ds.airQuality?.aqi != null) {
    rows.push({
      label: 'Air Quality',
      value: `AQI ${ds.airQuality.aqi}${ds.airQuality.category ? ` (${ds.airQuality.category})` : ''}`,
    });
  }
  if (ds.connectivity) {
    const ix = formatConnectivitySummary(ds.connectivity as ConnectivityData);
    if (ix) rows.push({ label: 'Nearest IX', value: ix });
    if (ds.connectivity.facilityCount50km != null) {
      rows.push({ label: 'DC Facilities <50km', value: String(ds.connectivity.facilityCount50km) });
    }
  }
  if (ds.nrcReactors) {
    if (ds.nrcReactors.nearestReactorName && ds.nrcReactors.nearestReactorDistKm != null) {
      const isNoReactorSentinel =
        ds.nrcReactors.nearestReactorDistKm >= 900 ||
        /^none\b/i.test(ds.nrcReactors.nearestReactorName.trim());
      rows.push({
        label: 'Nearest Reactor',
        value: isNoReactorSentinel
          ? ds.nrcReactors.nearestReactorName
          : `${ds.nrcReactors.nearestReactorName} (${ds.nrcReactors.nearestReactorDistKm} km)`,
      });
    }
    if (ds.nrcReactors.nuclearInfraMaturity != null) {
      rows.push({ label: 'Nuclear Infra', value: String(ds.nrcReactors.nuclearInfraMaturity) });
    }
  }
  if (ds.environmental?.wetlandAcreage != null) {
    rows.push({ label: 'Wetlands', value: `${ds.environmental.wetlandAcreage} acres` });
  }
  if (ds.parcel?.acreage != null) {
    rows.push({ label: 'Parcel Acreage', value: `${ds.parcel.acreage} acres` });
  }
  return rows;
}

/** Accordion-style subsection groups for DOCX extended data. */
export interface ExtendedDataSubsection {
  title: string;
  rows: ExtendedDataRow[];
}

export function buildExtendedDataSubsections(
  report: Parameters<typeof buildExtendedDataRows>[0],
): ExtendedDataSubsection[] {
  const ds: DatasetShape = report.datasets ?? {};
  const sections: ExtendedDataSubsection[] = [];

  const siteRows: ExtendedDataRow[] = [];
  if (report.address) siteRows.push({ label: 'Address', value: report.address });
  if (Number.isFinite(report.location?.lat) && Number.isFinite(report.location?.lng)) {
    siteRows.push({
      label: 'Coordinates',
      value: `${report.location!.lat!.toFixed(4)}, ${report.location!.lng!.toFixed(4)}`,
    });
  }
  if (report.siteArea != null && Number.isFinite(report.siteArea)) {
    siteRows.push({ label: 'Site Area', value: `${report.siteArea.toFixed(1)} acres` });
  }
  if (siteRows.length > 0) sections.push({ title: 'Site Boundary', rows: siteRows });

  if (ds.seismic) {
    const rows: ExtendedDataRow[] = [];
    if (ds.seismic.designCategory) rows.push({ label: 'Design Category', value: String(ds.seismic.designCategory) });
    if (Number.isFinite(ds.seismic.pga)) rows.push({ label: 'PGA', value: `${ds.seismic.pga!.toFixed(3)}g` });
    if (Number.isFinite(ds.seismic.sds)) rows.push({ label: 'SDS', value: `${ds.seismic.sds!.toFixed(3)}g` });
    if (Number.isFinite(ds.seismic.sd1)) rows.push({ label: 'SD1', value: `${ds.seismic.sd1!.toFixed(3)}g` });
    if (rows.length > 0) {
      sections.push({
        title: `Seismic Design (ASCE 7-22) – SDC ${ds.seismic.designCategory ?? '?'}`,
        rows,
      });
    }
  }

  if (ds.solar || ds.wind) {
    const rows: ExtendedDataRow[] = [];
    if (ds.solar?.ghi != null) rows.push({ label: 'Solar GHI', value: `${ds.solar.ghi} kWh/m²/day` });
    if (ds.solar?.dni != null) rows.push({ label: 'Solar DNI', value: `${ds.solar.dni} kWh/m²/day` });
    if (ds.wind?.windSpeed100m != null) rows.push({ label: 'Wind 100m', value: `${ds.wind.windSpeed100m} m/s` });
    if (ds.wind?.capacityFactor != null) {
      rows.push({ label: 'Capacity Factor', value: `${(ds.wind.capacityFactor * 100).toFixed(0)}%` });
    }
    if (rows.length > 0) sections.push({ title: 'Renewable Resource', rows });
  }

  if (ds.electricity) {
    sections.push({
      title: formatElectricityPricingTitle(ds.electricity),
      rows: [{ label: 'Price Reference', value: formatElectricityPriceReference(ds.electricity) }],
    });
  }

  if (ds.airQuality) {
    const rows: ExtendedDataRow[] = [];
    if (ds.airQuality.aqi != null) rows.push({ label: 'AQI', value: String(ds.airQuality.aqi) });
    if (ds.airQuality.category) rows.push({ label: 'Category', value: ds.airQuality.category });
    if (ds.airQuality.primaryPollutant) rows.push({ label: 'Primary Pollutant', value: ds.airQuality.primaryPollutant });
    if (rows.length > 0) {
      sections.push({
        title: `Air Quality – AQI ${ds.airQuality.aqi ?? '?'} (${ds.airQuality.category ?? ''})`.trim(),
        rows,
      });
    }
  }

  if (ds.environmental) {
    const rows: ExtendedDataRow[] = [];
    if (ds.environmental.hasWetlands != null || resolveWetlandAcreage(ds.environmental) != null) {
      rows.push({
        label: 'Wetlands',
        value: formatWetlandsDisplay(ds.environmental),
      });
    }
    if (ds.environmental.hasCriticalHabitat != null) {
      rows.push({
        label: 'Critical Habitat',
        value: ds.environmental.hasCriticalHabitat ? 'Present' : 'None',
      });
    }
    if (rows.length > 0) sections.push({ title: 'Environmental', rows });
  }

  if (ds.connectivity) {
    const rows: ExtendedDataRow[] = [];
    const ix = formatConnectivitySummary(ds.connectivity as ConnectivityData);
    if (ix) rows.push({ label: 'Nearest IX', value: ix });
    if (ds.connectivity.facilityCount50km != null) {
      rows.push({ label: 'DC Facilities <50km', value: String(ds.connectivity.facilityCount50km) });
    }
    if (rows.length > 0) sections.push({ title: 'Connectivity', rows });
  }

  if (ds.climate) {
    const rows: ExtendedDataRow[] = [];
    const avgTemp = formatDataValue(ds.climate.avgTemp, '°C');
    if (avgTemp) rows.push({ label: 'Avg Temperature', value: avgTemp });
    const peakTemp = formatDataValue(ds.climate.peakTemp, '°C');
    if (peakTemp) rows.push({ label: 'Peak Temperature', value: peakTemp });
    const avgWb = formatDataValue(ds.climate.avgWetBulb, '°C');
    if (avgWb) rows.push({ label: 'Avg Wet Bulb', value: avgWb });
    if (rows.length > 0) sections.push({ title: 'Climate', rows });
  }

  return sections;
}
